
import { useState, useEffect, useRef } from "react";
import { Modal, Input } from "antd";
import { AntdButton, DatePickerField, InputField, SelectInput } from "../../../../blueplanitUtils/InputField";
import { CustomGridTable } from "../../../../blueplanitUtils/CustomGridTable";
import dayjs from "dayjs";
import {
    createSubscriptionInvoice, setFormField, setFormProducts, setSelectedProduct, setLastSelectedProduct,
    setNewProductForm, resetForm, DEFAULT_COMPANYS_ADDRESS, fetchClients
} from "../../../../Slices/clientSlice";
import { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { S3ACCESSURL } from "../../../../databucket";
import { fetchInvoiceProducts, addInvoiceProductAsync } from "../../../../Slices/InvoiceSlice";


const Subscription = ({ agencyName, clientId, agencyData, openFromHeader, onCloseFromHeader }) => {
    const dispatch = useDispatch();
    const subscriptionForm = useSelector(state => state.subscriptionInvoice.form);
    const clients = useSelector(state => state.clients.clients);
    const toDateOnly = (date) =>
        date ? dayjs(date).startOf('day').toISOString()  : null;

    const {
        products,
        selectedProduct,
        price,
        qty,
        durationDate,
        endDate,
        lastSelectedProduct,
        newProductForm,
        billStarting,
        companyAddress,
        accountNumber,
        bankName,
        bankAddress,
    } = subscriptionForm;

    const [showAllPrices, setShowAllPrices] = useState(false);
    const [activeSubscriptionIndex, setActiveSubscriptionIndex] = useState(null);

    const [openSmall, setOpenSmall] = useState(true);
    const [openMain, setOpenMain] = useState(false);
    const [openProductModal, setOpenProductModal] = useState(false);
    const [showPricingModal, setShowPricingModal] = useState(false);
    const [durationType, setDurationType] = useState("endDate");
    const [billingCycles, setBillingCycles] = useState(1);
    const [productsList, setProducts] = useState(products || []);
    const [priceState, setPrice] = useState(price || "");
    const [qtyState, setQty] = useState(qty || 1);
    const visibleProducts = useMemo(() => {
        return subscriptionForm.products.filter(p => !p.isDeleted);
    }, [subscriptionForm.products]);

    const [newProductDescription, setNewProductDescription] = useState("");
    const [newProductImage, setNewProductImage] = useState(null);
    const [newProductTax, setNewProductTax] = useState(null);
    const [newProductName, setNewProductName] = useState("");
    const [newProductBillingType, setNewProductBillingType] = useState("");
    const [newProductBillingPeriod, setNewProductBillingPeriod] = useState("");
    const [newProductPrice, setNewProductPrice] = useState("");
    const [newProductQty, setNewProductQty] = useState();
    const [productRows, setProductRows] = useState([0]);
    const [mode, setMode] = useState("create");

    const [subtotal, setSubtotal] = useState(0);
    const [taxAmountCalc, setTaxAmount] = useState(0);
    const [grandTotalCalc, setGrandTotal] = useState(0);

    const [companyAddressOpen, setCompanyAddressOpen] = useState(false);
    const [paymentDetailsOpen, setPaymentDetailsOpen] = useState(false);

    const { products: masterProducts } = useSelector(state => state.invoiceProducts);

    const selectedProd = products.find(
        p => p.productId === lastSelectedProduct && !p.isDeleted
    );

    const updatedClient = useSelector(state => state.clients.clients.find(c => c.id === clientId));
    const subscriptions = updatedClient?.InvoiceDetails || [];
    const invoices = agencyData?.GeneratedInvoices || [];

    const subscriptionLoaded = useMemo(() => {
        return agencyData?.InvoiceDetails !== undefined;
    }, [agencyData]);

    const selectedClient = clients.find(
        c => c.id === subscriptionForm.clientId
    );

    const initDone = useRef(false);
    const prevClientId = useRef(null);
    const loadedSubscriptionRef = useRef(null);

    const disabledProductIds = products
        .map(p => p?.productId)
        .filter(Boolean);

    useEffect(() => {
        if (openFromHeader) {
            setMode("create");
            resetCreateState();
            setActiveSubscriptionIndex(null);
            loadedSubscriptionRef.current = null;
            dispatch(resetForm());
            setOpenSmall(false);
            setOpenMain(true);
        }
    }, [openFromHeader]);

    useEffect(() => {
        dispatch(fetchInvoiceProducts());
    }, [dispatch]);

    useEffect(() => {
        if (!subscriptionLoaded) return;
        if (clientId !== prevClientId.current) {
            const hasSub = agencyData?.InvoiceDetails?.length > 0;

            if (!hasSub) {
                dispatch(resetForm());
            }
            prevClientId.current = clientId;
        }
    }, [clientId, subscriptionLoaded, agencyData, dispatch]);


    useEffect(() => {
        if (!selectedProd) return;

        const priceValue = Number(price || selectedProd.price || 0);
        const qtyValue = Number(qty || selectedProd.qty || 1);
        const taxValue = Number(selectedProd.tax || 0);

        const subtotalCalc = priceValue * qtyValue;
        const taxCalc = subtotalCalc * (taxValue / 100);
        const grandCalc = subtotalCalc + taxCalc;

        setSubtotal(subtotalCalc);
        setTaxAmount(taxCalc);
        setGrandTotal(grandCalc);
    }, [selectedProd, price, qty]);

    useEffect(() => {
        if (mode !== "edit") return;
        if (!agencyData?.InvoiceDetails?.length || !clientId) return;
        if (!masterProducts.length) return;
        if (activeSubscriptionIndex === null) return;

        const sub = agencyData.InvoiceDetails[activeSubscriptionIndex];
        if (!sub) return;

        if (loadedSubscriptionRef.current === activeSubscriptionIndex) return;

        loadedSubscriptionRef.current = activeSubscriptionIndex;

        dispatch(setFormField({
            field: "companyAddress",
            value: sub.companyAddress || DEFAULT_COMPANYS_ADDRESS
        }));
        dispatch(setFormField({ field: "accountNumber", value: sub.accountNumber || "" }));
        dispatch(setFormField({ field: "bankName", value: sub.bankName || "" }));
        dispatch(setFormField({ field: "bankAddress", value: sub.bankAddress || "" }));

        const mappedProducts = sub.products.map((p) => {
            const master = masterProducts.find(
                (m) => m.name === p.name
            );
            return {
                productId: p.productId || master?.id || null,
                name: p.name,
                description: p.description,
                image: p.image,
                tax: p.tax,
                price: Number(p.price),
                qty: Number(p.qty),
                type: p.type,
                billingPeriod: p.billingPeriod,
                isDeleted: false,
            };
        });

        dispatch(setFormField({ field: "clientId", value: clientId }));
        dispatch(setFormProducts(mappedProducts));
        dispatch(setLastSelectedProduct(mappedProducts[0]?.productId || null));
        const validProductIds = mappedProducts
            .map(p => p.productId)
            .filter(Boolean);

        dispatch(setSelectedProduct(validProductIds));
        dispatch(setLastSelectedProduct(validProductIds[0] || null));
        setProductRows(mappedProducts.map((_, index) => index));

        const firstProd = mappedProducts[0];
        if (firstProd) {
            dispatch(setFormField({ field: "qty", value: firstProd.qty }));
            dispatch(setFormField({ field: "price", value: firstProd.price }));
        }

        dispatch(setFormField({ field: "durationDate", value: dayjs(+sub.fromDate) }));
        dispatch(setFormField({
            field: "endDate",
            value: sub.durationType === "forever"
                ? "Forever"
                : dayjs(+sub.endDate),
        }));
        dispatch(setFormField({ field: "billStarting", value: dayjs(+sub.billStarting) }));

    }, [activeSubscriptionIndex, agencyData, clientId, masterProducts, dispatch]);

    const handleDownload = async (url, invoiceNo) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();

            const ext = blob.type === "application/pdf" ? "pdf" : blob.type.split("/")[1] || "file";
            const filename = `Invoice-${invoiceNo}.${ext}`;

            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename;

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed", err);
            alert("Unable to download file");
        }
    };

    const handleCreateSubscription = async () => {
        const updatedProducts = products
            .filter(p => !p.isDeleted)
            .map(p =>
                p.productId === selectedProduct
                    ? { ...p, price: Number(price), qty: Number(qty) }
                    : p
            );
        let formattedEndDate = null;

        if (endDate && endDate !== "Forever") {
            formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");
        }
        const isOneOff = updatedProducts.every(p => p.type === "one-off");

        const invoiceInput = {
            customerName: agencyData?.clientName || "--",
            fromDate: isOneOff ? null : toDateOnly(durationDate),
            endDate: isOneOff ? null : dayjs(formattedEndDate).endOf('day').toISOString() ,
            durationType,
            // billingCycles: Number(billingCycles),
            billStarting: toDateOnly(billStarting || durationDate || dayjs()),
            createdBy: "Admin",
            companyAddress: companyAddress,
            accountNumber: accountNumber,
            bankName: bankName,
            bankAddress: bankAddress,

            products: updatedProducts.map(p => {
                const total = Number(p.price) * Number(p.qty);
                const taxAmount = total * (Number(p.tax || 0) / 100);
                const grand = total + taxAmount;

                return {
                    productId: p.productId,
                    name: p.name,
                    qty: Number(p.qty || 1),
                    type: p.type,
                    billingPeriod: p.billingPeriod,
                };
            })
        };

        const isEdit = activeSubscriptionIndex !== null;
        try {
            const response = await dispatch(
                createSubscriptionInvoice({
                    clientId,
                    input: invoiceInput,
                    isEdit,
                    index: activeSubscriptionIndex,
                })
            ).unwrap();
            await dispatch(fetchClients());

            dispatch(
                setSelectedProduct(
                    Array.isArray(selectedProduct)
                        ? selectedProduct
                        : selectedProduct
                            ? [selectedProduct]
                            : []
                )
            );

            dispatch(setLastSelectedProduct(
                Array.isArray(selectedProduct)
                    ? selectedProduct[selectedProduct.length - 1]
                    : selectedProduct
            ));

            dispatch(setFormProducts(updatedProducts));
            alert(
                isOneOff
                    ? "Invoice generated"
                    : isEdit
                        ? "Subscription updated!"
                        : "Subscription created!"
            );
            setOpenMain(false);
            setOpenSmall(true);
            onCloseFromHeader?.();

        } catch (error) {
            console.error("GraphQL Errors:", error.graphQLErrors);
            alert(error.graphQLErrors?.[0]?.message || "Failed to create invoice");
        }
    };

    const selectedIds = (
        Array.isArray(selectedProduct)
            ? selectedProduct
            : selectedProduct
                ? [selectedProduct]
                : []
    ).filter(id => {
        const prod = products.find(p => p.productId === id);
        return prod && !prod.isDeleted;
    });

    const activeProducts = products.filter(p => !p.isDeleted);
    const isOneOff =
        activeProducts.length > 0 &&
        activeProducts.every(p => p.type === "one-off");

    const handleRemoveProduct = (rowIndex) => {
        const nextProducts = [...products];
        if (nextProducts[rowIndex]) {
            nextProducts[rowIndex] = {
                ...nextProducts[rowIndex],
                isDeleted: true,
            };
        }
        dispatch(setFormProducts(nextProducts));
        const nextSelectedIds = [...selectedIds];
        nextSelectedIds[rowIndex] = undefined;

        dispatch(setSelectedProduct(nextSelectedIds.filter(Boolean)));
        setProductRows(prev =>
            prev.filter(index => index !== rowIndex)
        );
        const remaining = nextProducts.filter(p => !p.isDeleted);

        if (remaining.length > 0) {
            dispatch(setLastSelectedProduct(remaining[0].productId));
            dispatch(setFormField({ field: "qty", value: remaining[0].qty }));
            dispatch(setFormField({ field: "price", value: remaining[0].price }));
        } else {
            dispatch(setLastSelectedProduct(null));
        }
    };

    const resetCreateState = () => {
        setMode("create");
        setActiveSubscriptionIndex(null);
        loadedSubscriptionRef.current = null;
        setProductRows([0]);
        dispatch(resetForm());
        dispatch(setSelectedProduct([]));
        dispatch(setLastSelectedProduct(null));
    };

    const getBaseDate = () => {
        if (dayjs.isDayjs(durationDate)) return durationDate;
        return dayjs();
    };

    const generatedInvoiceColumns = [
        {
            title: "Invoice No",
            key: "invoiceNo",
            align: "center",
            render: (_, inv, index) =>
                inv.invoiceNo || `INV-${index + 1}`,
        },
        {
            title: "Subscription No",
            key: "subscriptionNo",
            align: "center",
            render: (_, inv) =>
                Number.isInteger(inv.subscriptionIndex)
                    ? `Subscription - ${inv.subscriptionIndex + 1}`
                    : "--",
        },
        {
            title: "Client Name",
            key: "clientName",
            align: "center",
            render: () =>
                agencyData?.clientName || agencyName || "--",
        },
        {
            title: "Billing Start Date",
            key: "billingStartDate",
            align: "center",
            render: (_, inv) =>
                inv.billStarting
                    ? dayjs(inv.billStarting).format("MMM DD, YYYY")
                    : agencyData?.InvoiceDetails?.[inv.subscriptionIndex]?.billStarting
                        ? dayjs(
                            Number(
                                agencyData.InvoiceDetails[inv.subscriptionIndex]
                                    .billStarting
                            )
                        ).format("MMM DD, YYYY")
                        : "--",
        },
        {
            title: "Invoice Generated Date",
            key: "invoiceDate",
            align: "center",
            render: (_, inv) =>
                inv.invoiceDate
                    ? dayjs(Number(inv.invoiceDate)).format("MMM DD, YYYY")
                    : "--",
        },
        {
            title: "Sub Total",
            key: "subTotal",
            align: "center",
            render: (_, inv) =>
                `$${(inv.subTotal ?? 0).toFixed(2)}`,
        },
        {
            title: "Tax",
            key: "taxTotal",
            align: "center",
            render: (_, inv) =>
                `$${(inv.taxTotal ?? 0).toFixed(2)}`,
        },
        {
            title: "Grand Total",
            key: "grandTotal",
            align: "center",
            render: (_, inv) =>
                `$${(inv.grandTotal ?? 0).toFixed(2)}`,
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, inv, index) => {
                const invoiceNo = inv.invoiceNo || `INV-${index + 1}`;
                return inv.invoiceImage ? (
                    <>
                        <a
                            href={S3ACCESSURL + inv.invoiceImage}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ marginRight: 8 }}
                        >
                            Preview
                        </a>

                        <span
                            style={{ cursor: "pointer", color: "#1677ff" }}
                            onClick={() =>
                                handleDownload(
                                    S3ACCESSURL + inv.invoiceImage,
                                    `Invoice-${invoiceNo}.pdf`
                                )
                            }
                        >
                            Download
                        </span>
                    </>
                ) : (
                    <span style={{ color: "#999", fontSize: 12 }}>
                        No PDF
                    </span>
                );
            },
        },
    ];

    const computeSubscriptionStatus = (sub) => {
        const today = dayjs().startOf("day");
        const isOneOff =
            sub.products?.length &&
            sub.products.every(p => p.type === "one-off");

        if (isOneOff) return "Inactive";
        if (
            sub.billingCycles != null &&
            Number(sub.cyclesGenerated || 0) >= Number(sub.billingCycles)
        ) {
            return "Inactive";
        }
        if (sub.endDate && dayjs(+sub.endDate).isBefore(today)) {
            return "Inactive";
        }
        return "Active";
    };


    return (
        <>
            {subscriptions.length > 0 && (
                <div className="card mb-3" style={{ padding: "12px 16px" }}>
                    {subscriptions.map((sub, index) => {
                        const status = sub.status ?? computeSubscriptionStatus(sub);
                        console.log("SUB STATUS:", sub.status);
                        return (<div
                            key={index}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "8px 0",
                                borderBottom: "1px solid #eee"
                            }}
                        >
                            <div>
                                <div style={{ fontSize: "13px", fontWeight: 600 }}>
                                    Subscription : {index + 1}
                                    <span
                                        style={{
                                            // marginLeft: 14,
                                            fontSize: "11px",
                                            padding: "2px 8px",
                                            borderRadius: "12px",
                                            background: status === "Active" ? "#e6f4ea" : "#fdecea",
                                            color: status === "Active" ? "#137333" : "#a61d24",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {status}
                                    </span>
                                </div>
                                <div style={{ fontSize: "12px", color: "#666", marginTop: 2 }}>
                                    <span style={{ marginRight: 24 }}>
                                        <b>Billing Start:</b>{" "}
                                        {sub.billStarting
                                            ? dayjs(Number(sub.billStarting)).format("MMM DD, YYYY")
                                            : "--"}
                                    </span>
                                    <span style={{ marginRight: 24 }}>
                                        <b>From:</b>{" "}
                                        {sub.fromDate
                                            ? dayjs(Number(sub.fromDate)).format("MMM DD, YYYY")
                                            : "--"}
                                    </span>
                                    <span style={{ marginRight: 24 }}>
                                        <b>End:</b>{" "}
                                        {sub.endDate === "Forever"
                                            ? "Forever"
                                            : sub.endDate
                                                ? dayjs(Number(sub.endDate)).format("MMM DD, YYYY")
                                                : "--"}
                                    </span>
                                    <span>
                                        <b>Frequency:</b>{" "}
                                        {(() => {
                                            const prod = sub.products?.[0];
                                            if (!prod) return "--";
                                            if (prod.type === "one-off") return "One-off";
                                            return prod.billingPeriod
                                                ? prod.billingPeriod.charAt(0).toUpperCase() + prod.billingPeriod.slice(1)
                                                : "--";
                                        })()}
                                    </span>
                                </div>
                            </div>

                            <AntdButton
                                size="small"
                                label="Edit"
                                onClick={() => {
                                    setMode("edit");
                                    loadedSubscriptionRef.current = null;
                                    setActiveSubscriptionIndex(index);
                                    setOpenSmall(false);
                                    setOpenMain(true);
                                }}
                            />
                        </div>)
                    })}
                </div>
            )}

            {/* ================= INVOICE LIST (TABLE STYLE) ================= */}
            {invoices.length > 0 && (
                <div
                    className="card mb-3"
                    style={{ marginTop: "20px", padding: "12px 16px" }}
                >
                    <h5
                        className="mb-3"
                        style={{ fontSize: "15px", fontWeight: "600" }}
                    >
                        Generated Invoices
                    </h5>

                    <CustomGridTable
                        Id="generated-invoices-with-subscription"
                        columns={generatedInvoiceColumns}
                        dataSource={invoices}
                        rowKey={(record, index) =>
                            `${record.invoiceNo}-${record.invoiceDate}-${index}`
                        }
                        pagination={false}
                        size="small"
                        enableColumnResize
                        enableColumnToggle
                        disableColumnDrag={false}
                    />
                </div>
            )}


            <Modal
                title={
                    <div style={{ fontSize: "16px", fontWeight: 600 }}>
                        {activeSubscriptionIndex !== null
                            ? `Edit Subscription #${activeSubscriptionIndex + 1}`
                            : "Create Subscription"}
                    </div>
                }
                open={openMain}
                centered
                width={500}
                height={450}
                onCancel={() => {
                    setOpenMain(false);
                    setOpenSmall(true);
                    onCloseFromHeader?.();
                    resetCreateState();
                    dispatch(
                        setSelectedProduct(
                            Array.isArray(subscriptionForm.selectedProduct)
                                ? subscriptionForm.selectedProduct
                                : lastSelectedProduct
                                    ? [lastSelectedProduct]
                                    : []
                        )
                    );
                }}

                footer={null}
            >
                <div
                    className="mb-2"
                    style={{
                        display: "flex",
                        gap: "20px",
                        alignItems: "flex-start"
                    }}
                >
                    <div style={{ flex: 1 }}>
                        <label style={{ fontSize: "12px", marginBottom: "4px" }}>
                            Customer
                        </label>
                        <InputField
                            value={agencyName}
                            disabled
                            style={{ height: 30 }}
                        />
                    </div>

                    <div style={{ flex: 1 }}>
                        <label style={{ fontSize: "12px", marginBottom: "4px" }}>
                            Bill starting
                        </label>
                        <DatePickerField
                            value={billStarting}
                            onChange={(date) =>
                                dispatch(setFormField({ field: "billStarting", value: date }))
                            }
                            format="DD/MM/YYYY"
                            style={{ width: "100%", height: 30 }}
                        />
                        <p className="text-muted small mt-1">
                            This is when the first invoice will be generated.
                        </p>
                    </div>
                </div>

                {!isOneOff && (
                    <>
                        <h6 style={{ fontSize: "12px", fontWeight: 600, marginTop: "20px" }}>
                            Subscription details
                        </h6>
                        <div className="mt-2" style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>

                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: "12px", marginBottom: "4px" }}>
                                    From date
                                </label>
                                <DatePickerField
                                    value={durationDate}
                                    onChange={(date) => dispatch(setFormField({ field: "durationDate", value: date }))}
                                    style={{ width: "100%", height: 30 }}
                                    format="DD/MM/YYYY"
                                />
                            </div>

                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: "12px", marginBottom: "4px" }}>
                                    End date
                                </label>
                                <DatePickerField
                                    value={dayjs.isDayjs(endDate) ? endDate : null}
                                    onChange={(date) =>
                                        dispatch(setFormField({ field: "endDate", value: date }))
                                    }
                                    style={{ width: "100%", height: 30 }}
                                    format="DD/MM/YYYY"
                                    inputReadOnly
                                    placement="topLeft"
                                    getPopupContainer={() => document.body}
                                    popupClassName="compact-datepicker"
                                    panelRender={(panel) => (
                                        <div className="calendar-wrapper">
                                            <div className="calendar-left">{panel}</div>

                                            <div className="calendar-shortcuts">
                                                <span
                                                    className="calendar-link"
                                                    onClick={() =>
                                                        dispatch(setFormField({ field: "endDate", value: "Forever" }))
                                                    }
                                                >
                                                    Forever
                                                </span>

                                                <span
                                                    className="calendar-link"
                                                    onClick={() =>
                                                        dispatch(setFormField({ field: "endDate", value: dayjs() }))
                                                    }
                                                >
                                                    Today
                                                </span>

                                                <span
                                                    className="calendar-link"
                                                    onClick={() =>
                                                        dispatch(
                                                            setFormField({
                                                                field: "endDate",
                                                                value: getBaseDate().add(1, "month"),
                                                            })
                                                        )
                                                    }
                                                >
                                                    Next month
                                                </span>

                                                <span
                                                    className="calendar-link"
                                                    onClick={() =>
                                                        dispatch(
                                                            setFormField({
                                                                field: "endDate",
                                                                value: getBaseDate().add(6, "months"),
                                                            })
                                                        )
                                                    }
                                                >
                                                    6 months
                                                </span>

                                                <span
                                                    className="calendar-link"
                                                    onClick={() =>
                                                        dispatch(
                                                            setFormField({
                                                                field: "endDate",
                                                                value: getBaseDate().add(1, "year"),
                                                            })
                                                        )
                                                    }
                                                >
                                                    1 year
                                                </span>

                                                <span
                                                    className="calendar-link"
                                                    onClick={() =>
                                                        dispatch(setFormField({ field: "endDate", value: null }))
                                                    }
                                                >
                                                    Until date
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                />
                            </div>

                        </div>
                    </>
                )}

                <div className="mt-4">
                    <h6 style={{ fontSize: "12px", fontWeight: 600, marginTop: "20px" }}>
                        Pricing
                    </h6>
                    <div className="mt-2">
                        <div style={{ width: "100%" }}>
                            <label
                                style={{
                                    fontSize: "12px",
                                    marginBottom: "4px",
                                    display: "block"
                                }}
                            >
                                Product name
                            </label>

                            {productRows.map((rowIndex) => (
                                <div key={rowIndex} className="d-flex align-items-center">
                                    <SelectInput
                                        placeholder="Select a product"
                                        style={{ marginRight: "10px", marginBottom: "6px", width:"215px", minWidth:"215px" }}
                                        value={selectedIds?.[rowIndex] || undefined}
                                        optionLabelProp="name"
                                        options={masterProducts.map((p) => {
                                            const isSelectedInOtherRow =
                                                disabledProductIds.includes(p.id) &&
                                                selectedIds?.[rowIndex] !== p.id;
                                            return {
                                                value: p.id,
                                                name: p.name,
                                                disabled: isSelectedInOtherRow,
                                                label: (
                                                    <div style={{ opacity: isSelectedInOtherRow ? 0.5 : 1 }}>
                                                        <b>{p.name}</b>
                                                        <div style={{ fontSize: "12px", color: "#555" }}>
                                                            US ${p.price} / {p.billingPeriod || "one-off"}
                                                        </div>
                                                    </div>
                                                )
                                            };
                                        })}
                                        dropdownRender={(menu) => (
                                            <>
                                                {menu}
                                                <div
                                                    style={{
                                                        padding: "8px",
                                                        cursor: "pointer",
                                                        color: "#635BFF",
                                                        fontWeight: 500,
                                                        minWidth:"215px"
                                                    }}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => setOpenProductModal(true)}
                                                >
                                                    ➕ Add Product
                                                </div>
                                            </>
                                        )}
                                        onChange={(value) => {
                                            if (!value) return;

                                            const master = masterProducts.find(p => p.id === value);
                                            if (!master) return;
                                            const nextSelectedIds = [...selectedIds];
                                            nextSelectedIds[rowIndex] = value;

                                            dispatch(setSelectedProduct(nextSelectedIds));
                                            const nextProducts = [...products];

                                            nextProducts[rowIndex] = {
                                                productId: master.id,
                                                name: master.name,
                                                price: Number(master.price),
                                                tax: Number(master.tax || 0),
                                                qty: Number(master.qty || 1),
                                                type: master.type,
                                                billingPeriod: master.billingPeriod,
                                                isDeleted: false
                                            };

                                            dispatch(setFormProducts(nextProducts));
                                            dispatch(setLastSelectedProduct(master.id));
                                            dispatch(setFormField({ field: "qty", value: master.qty }));
                                            dispatch(setFormField({ field: "price", value: master.price }));
                                        }}

                                    />
                                    <>
                                        <InputField
                                            type="number"
                                            className="ms-2"
                                            style={{ width: "105px", marginBottom: '22px', marginLeft: '30px' }}
                                            value={products[rowIndex]?.qty || ""}
                                            disabled
                                        />
                                        <InputField
                                            prefix="US$"
                                            className="ms-2"
                                            style={{ width: "105px", marginBottom: '22px', marginLeft: '10px' }}
                                            value={products[rowIndex]?.price || ""}
                                            disabled
                                        />
                                    </>
                                    <span
                                        style={{
                                            marginLeft: "10px",
                                            cursor: "pointer",
                                            color: "#ff4d4f",
                                            fontSize: "14px",
                                            marginBottom: '22px'
                                        }}
                                        onClick={() => handleRemoveProduct(rowIndex)}
                                    >
                                        ✕
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <button
                        type="button"
                        style={{
                            background: "transparent",
                            border: "none",
                            color: "#635BFF",
                            fontSize: "11px",
                            padding: 0,
                            marginBottom: "6px",
                            cursor: "pointer",
                            fontWeight: 500
                        }}
                        onClick={() => {
                            setProductRows(prev => {
                                const nextIndex = prev.length ? Math.max(...prev) + 1 : 0;
                                return [...prev, nextIndex]
                            });
                        }}
                    >
                        + Add Product
                    </button>
                </div>

                {/* ================= COMPANY ADDRESS & PAYMENT DETAILS ================= */}
                <div className="mt-3" style={{ display: "flex", gap: "16px", marginBottom: "-20px" }}>
                    <div style={{ flex: 1 }}>
                        <div
                            style={{ cursor: "pointer", fontSize: "12px", marginBottom: "4px" }}
                            onClick={() => setCompanyAddressOpen(prev => !prev)}
                        >
                            Company Address {companyAddressOpen ? "▲" : "▼"}
                        </div>
                        {companyAddressOpen && (
                            <Input.TextArea
                                rows={7}
                                value={companyAddress}
                                onChange={(e) => dispatch(setFormField({ field: "companyAddress", value: e.target.value }))}
                                placeholder={`Line 1: .\nLine 2: \nCity: , \nCountry: \nPhone: `}
                                style={{ fontSize: "12px", resize: "none", height: "65%", width: "100%" }}
                            />
                        )}
                    </div>

                    <div style={{ flex: 1 }}>
                        <div
                            style={{ cursor: "pointer", fontSize: "12px", marginBottom: "4px" }}
                            onClick={() => setPaymentDetailsOpen(prev => !prev)}
                        >
                            Payment Details {paymentDetailsOpen ? "▲" : "▼"}
                        </div>
                        {paymentDetailsOpen && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                <InputField
                                    placeholder="Account Number"
                                    value={accountNumber}
                                    onChange={(e) => dispatch(setFormField({ field: "accountNumber", value: e.target.value }))}
                                    style={{ fontSize: "12px", height: 30 }}
                                />
                                <InputField
                                    placeholder="Bank Name"
                                    value={bankName}
                                    onChange={(e) => dispatch(setFormField({ field: "bankName", value: e.target.value }))}
                                    style={{ fontSize: "12px", height: 30 }}
                                />
                                <InputField
                                    placeholder="Bank Address"
                                    value={bankAddress}
                                    onChange={(e) => dispatch(setFormField({ field: "bankAddress", value: e.target.value }))}
                                    style={{ fontSize: "12px", height: 30 }}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="text-end mt-4">
                    <AntdButton
                        variant="solid"
                        size="medium"
                        color="success"
                        label={isOneOff ? "Generate Invoice" : activeSubscriptionIndex !== null
                            ? "Update Subscription" : "Create Subscription"}
                        onClick={handleCreateSubscription}
                    />
                </div>
            </Modal>

            <Modal
                title="Add a Product"
                open={openProductModal}
                centered
                width={500}
                height={450}
                onCancel={() => setOpenProductModal(false)}
                footer={null}
            >
                <div className="two-field-row mb-3">
                    <div className="field">
                        <label style={{ fontSize: "12px", marginBottom: "4px" }}>Name <span style={{ color: "red" }}>*</span></label>
                        <InputField
                            placeholder="Name of the product"
                            value={newProductName}
                            onChange={(e) => setNewProductName(e.target.value)}
                            style={{ height: 30 }}
                        />
                    </div>

                    <div className="field">
                        <label style={{ fontSize: "12px", marginBottom: "4px" }}>Image</label>
                        <InputField
                            type="file"
                            onChange={(e) => setNewProductImage(e.target.files[0])}
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label style={{ fontSize: "12px", marginBottom: "4px" }}>Description</label>
                    <Input.TextArea
                        rows={3}
                        value={newProductDescription}
                        onChange={(e) => setNewProductDescription(e.target.value)}
                        style={{ height: 50 }}
                    />
                </div>
                <h6 style={{ fontSize: "12px", fontWeight: 600, marginTop: "20px" }}>
                    Pricing
                </h6>
                <div className="mt-2" style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>

                    <div style={{ flex: 1 }}>
                        {/* AMOUNT */}
                        <div className="mb-3">
                            <label style={{ fontSize: "12px", marginBottom: "4px" }}>Amount <span style={{ color: "red" }}>*</span></label>
                            <InputField
                                prefix="US$"
                                placeholder="0.00"
                                type="number"
                                min={1}
                                value={newProductPrice}
                                onChange={(e) => setNewProductPrice(e.target.value)}
                                style={{ height: 30 }}
                            />
                        </div>
                    </div>

                    <div style={{ flex: 1 }}>
                        <div className="field">
                            <label style={{ fontSize: "12px", marginBottom: "4px" }}>Quantity <span style={{ color: "red" }}>*</span></label>
                            <InputField
                                type="number"
                                min={1}
                                value={newProductQty}
                                onChange={(e) => setNewProductQty(e.target.value)}
                                style={{ height: 30 }}
                            />
                        </div>
                    </div>
                </div>

                <div className="two-field-row mb-3">
                    <div className="field">
                        <label style={{ fontSize: "12px", marginBottom: "4px" }}>Product Tax Code</label>
                        <SelectInput
                            value={newProductTax}
                            onChange={(value) => setNewProductTax(Number(value))}
                            options={[
                                { label: "GST 0%", value: "0" },
                                { label: "GST 5%", value: "5" },
                                { label: "GST 12%", value: "12" },
                                { label: "GST 18%", value: "18" },
                                { label: "GST 28%", value: "28" },
                            ]}
                            style={{ height: 30 }}
                        />
                    </div>

                    {/* BILLING TYPE */}
                    <div className="field">
                        <label style={{ fontSize: "12px", marginBottom: "4px" }}>Billing Type <span style={{ color: "red" }}>*</span></label>
                        <SelectInput
                            style={{ width: "100%", height: 30 }}
                            value={newProductBillingType}
                            onChange={setNewProductBillingType}
                            options={[
                                { label: "Recurring", value: "recurring" },
                                { label: "One-off", value: "one-off" }
                            ]}
                        />
                    </div>
                </div>

                <div className="two-field-row mb-3">
                    {newProductBillingType === "recurring" && (
                        <div className="field">
                            <label>Billing Period
                                <span style={{ color: "red" }}>*</span></label>
                            <SelectInput
                                style={{ width: "48%", height: 30 }}
                                value={newProductBillingPeriod}
                                onChange={setNewProductBillingPeriod}
                                options={[
                                    { label: "Daily", value: "daily" },
                                    { label: "Weekly", value: "weekly" },
                                    { label: "Monthly", value: "monthly" },
                                    { label: "Quarterly", value: "quarterly" },
                                    { label: "Semi-Annual", value: "semi-annual" },
                                    { label: "Yearly", value: "yearly" },
                                ]}
                            />
                        </div>
                    )}


                </div>

                {/* ADD PRODUCT BUTTON */}
                <div className="text-end mt-4">
                    <AntdButton
                        variant="solid"
                        size="medium"
                        label="Add Product"
                        color="success"
                        onClick={async () => {
                            // ---------- VALIDATION ----------
                            if (!newProductName.trim()) return alert("Product name is required");
                            if (!newProductPrice || newProductPrice <= 0) return alert("Valid price required");
                            if (!newProductQty || newProductQty <= 0) return alert("Quantity required");
                            if (newProductBillingType === "recurring" && !newProductBillingPeriod)
                                return alert("Billing period required");

                            try {
                                const createdProduct = await dispatch(
                                    addInvoiceProductAsync({
                                        name: newProductName,
                                        description: newProductDescription,
                                        price: Number(newProductPrice),
                                        qty: Number(newProductQty),
                                        tax: Number(newProductTax || 0),
                                        type: newProductBillingType,
                                        image: newProductImage?.name || null,
                                        ...(newProductBillingType === "recurring" && {
                                            billingPeriod: newProductBillingPeriod,
                                        })
                                    })
                                ).unwrap();

                                const subscriptionProduct = {
                                    productId: createdProduct.id,
                                    name: createdProduct.name,
                                    price: createdProduct.price,
                                    tax: createdProduct.tax,
                                    qty: createdProduct.qty,
                                    type: createdProduct.type,
                                    billingPeriod: createdProduct.billingPeriod,
                                    isDeleted: false,
                                };

                                const newIndex = products.length;

                                const rowIndex = productRows[productRows.length - 1];

                                /* 1️⃣ Insert product into that row */
                                const nextProducts = [...products];
                                nextProducts[rowIndex] = subscriptionProduct;
                                dispatch(setFormProducts(nextProducts));

                                /* 2️⃣ Select product for same row */
                                const nextSelected = [...selectedIds];
                                nextSelected[rowIndex] = createdProduct.id;
                                dispatch(setSelectedProduct(nextSelected));

                                /* 3️⃣ Pricing focus */
                                dispatch(setLastSelectedProduct(createdProduct.id));
                                dispatch(setFormField({ field: "qty", value: subscriptionProduct.qty }));
                                dispatch(setFormField({ field: "price", value: subscriptionProduct.price }));

                                setOpenProductModal(false);

                                setNewProductName("");
                                setNewProductDescription("");
                                setNewProductImage(null);
                                setNewProductTax(null);
                                setNewProductPrice("");
                                setNewProductBillingType("");
                                setNewProductBillingPeriod("");
                                setNewProductQty(1);
                            } catch (err) {
                                console.error(err);
                                alert(err.message || "Failed to add product");
                            }
                        }}
                    />
                </div>
            </Modal>
        </>
    );
};

export default Subscription;
